<template>
  <div>
    <div class="wallbox-list">
      <div v-if="loading" class="my-10 text-center">
        <v-progress-circular indeterminate :size="70" color="primary">
        </v-progress-circular>
        <div class="mt-5">{{ $t('wallboxes.loadingMessage') }}</div>
      </div>
      <div v-else-if="loadingError" class="my-10">
        <v-alert type="error">{{ $t('wallboxes.loadingError') }}</v-alert>
      </div>
      <template v-else>
        <div v-if="wallboxes.length" class="mb-6">
          <gc-row>
            <gc-col
              v-for="wallbox in wallboxes"
              :key="wallbox.id"
              cols="12"
              sm="6"
              class="d-flex"
            >
              <gc-card outlined>
                <gc-image
                  class="gc-cursor-pointer"
                  :height="200"
                  contain
                  :src="`${productUrl(wallbox)}`"
                  @click="openProduct(wallbox)"
                >
                  <template v-slot:placeholder>
                    <gc-row class="fill-height ma-0" justify="center">
                      <v-progress-circular
                        indeterminate
                        color="grey darken-4"
                      ></v-progress-circular>
                    </gc-row>
                  </template>
                </gc-image>
                <div class="pa-3">
                  <strong
                    class="d-block text-no-wrap text-truncate gc-cursor-pointer"
                    @click="openProduct(wallbox)"
                  >
                    {{ wallbox.translated.name }}
                  </strong>
                  <div
                    class="d-block gc-cursor-pointer"
                    @click="openProduct(wallbox)"
                  ></div>
                  <gc-button
                    depressed
                    color="grey lighten-3"
                    small
                    block
                    class="mt-2"
                    @click="openProduct(wallbox)"
                  >
                    <font-awesome-icon :icon="faInfoCircle" class="mr-2" />
                    {{ $t('wallboxes.moreInformation') }}
                  </gc-button>
                </div>
              </gc-card>
            </gc-col>
          </gc-row>
        </div>
        <gc-alert v-else type="info" text>
          {{ $t('wallboxes.noProductsFound') }}
        </gc-alert>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent, ref } from '@vue/composition-api';
import {
  faCheck,
  faInfoCircle,
  faPlus,
  faUndo,
} from '@fortawesome/pro-solid-svg-icons';
import {
  EqualsFilter,
  SearchFilterType,
} from '@shopware-pwa/commons/interfaces';
import GcImage from '@/components/primitives/GcImage.vue';
import GcRow from '@/components/primitives/GcRow.vue';
import GcCol from '@/components/primitives/GcCol.vue';
import GcCard from '@/components/primitives/GcCard.vue';
import GcButton from '@/components/primitives/GcButton.vue';
import { formatPrice } from '@/lib/formatter/price';
import GcTextField from '@/components/primitives/GcTextField.vue';
import GcSlider from '@/components/primitives/GcSlider.vue';
import GcCombobox from '@/components/primitives/GcCombobox.vue';
import GcAlert from '@/components/primitives/GcAlert.vue';
import {
  GcSwProduct,
  loadProductsByFilter,
} from '@/lib/greenportal/load-products';
import { AssistantContext } from '~/steps/steps';

export default defineComponent({
  components: {
    GcAlert,
    GcButton,
    GcCard,
    GcCol,
    GcCombobox,
    GcImage,
    GcRow,
    GcSlider,
    GcTextField,
  },
  props: {
    assistantContext: {
      required: true,
      type: Object as PropType<AssistantContext>,
    },
  },
  setup: (props) => {
    const loading = ref<boolean>(true);
    const loadingError = ref<null | boolean>(null);

    const wallboxes = ref<GcSwProduct[]>([]);

    const baseUrl = process.env.VUE_APP_BASE_URL;
    const shopUrl = process.env.VUE_APP_STAWAG_HOST;
    const categoryId = process.env.VUE_APP_SHOP_CATEGORY_UUID;

    const wantsOutdoor =
      props.assistantContext.location.lisArea.includes('outdoorArea');
    const wantsBillable =
      props.assistantContext.information.settleUser.includes('yes');

    const categoryFilter: EqualsFilter = {
      type: SearchFilterType.EQUALS,
      field: 'product.categoriesRo.id',
      value: categoryId ?? null,
    };

    loadProductsByFilter({
      filter: [categoryFilter],
      associations: {
        properties: {
          associations: {
            group: {},
          },
        },
      },
    })
      .then((data) => {
        const hasProperty = (
          wallbox: GcSwProduct,
          groupName: string,
          optionName: string,
        ) =>
          (wallbox.properties ?? []).some(
            (p) => p.group?.name === groupName && p.name === optionName,
          );

        wallboxes.value = data.filter((wallbox) => {
          if (wantsOutdoor && !hasProperty(wallbox, 'Outdoor-geeignet', 'Ja')) {
            return false;
          }
          if (
            wantsBillable &&
            !hasProperty(wallbox, 'Abrechnungsfähig', 'Ja')
          ) {
            return false;
          }
          return true;
        });
      })
      .catch((error) => {
        // eslint-disable-next-line
        console.log(error);
        loadingError.value = true;
      })
      .finally(() => (loading.value = false));

    const productUrl = (wallbox: GcSwProduct) =>
      wallbox.cover?.media?.url
        ? wallbox.cover?.media?.url
        : `${baseUrl}/image-placeholder.svg`;

    const productLink = (wallbox: GcSwProduct) =>
      `${shopUrl}/detail/${wallbox.id}`;

    const openProduct = (wallbox: GcSwProduct) => {
      window.open(productLink(wallbox), '_blank', 'noopener');
    };

    return {
      baseUrl,
      faCheck,
      faInfoCircle,
      faPlus,
      faUndo,
      formatPrice,
      openProduct,
      productUrl,
      productLink,
      loading,
      loadingError,
      wallboxes,
    };
  },
});
</script>

<style lang="scss">
.wallbox-list {
  .max-w-20 {
    max-width: 80px;
  }
  .card--selected {
    border: 1px solid var(--v-primary-base);
  }
}
</style>
